(function () {
  'use strict';

  const BOGO_PROP = '_bogo_free';
  const BOGO_RULE_PROP = '_bogo_rule';

  class BogoManager {
    constructor() {
      this.rules = window.bogoRules || [];
      this.isSyncing = false;
      this.cachedCart = null;

      if (!this.rules.length) return;

      // Pre-load cart state so the fetch interceptor can act on the first add
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((c) => { this.cachedCart = c; })
        .catch(() => {});

      // Patch window.fetch so free items are included in the original add request,
      // making them appear in the cart drawer immediately — no second round-trip.
      this._interceptFetch();

      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const cartData = event?.cartData;
        if (cartData?.items) this.cachedCart = cartData;
        this.sync(cartData?.items ? cartData : null);
      });

      this.sync();
    }

    // ---------------------------------------------------------------------------
    // Fetch interception — batches free items with the original product-form add
    // ---------------------------------------------------------------------------

    _interceptFetch() {
      const self = this;
      const _fetch = window.fetch.bind(window);

      window.fetch = function (url, init, ...rest) {
        // Only intercept single-item adds from product forms.
        // Multi-item adds (body.items array) come from BOGO itself — skip them.
        if (url === routes.cart_add_url && init?.method === 'POST') {
          try {
            const body = JSON.parse(init.body || '{}');
            if (!body.items && self.cachedCart) {
              const variantId = Number(body.id);
              const qty = Number(body.quantity) || 1;
              const bogoItems = self._getBogoItemsForAdd(variantId, qty);
              if (bogoItems.length > 0) {
                const mainItem = { id: variantId, quantity: qty };
                if (body.properties) mainItem.properties = body.properties;
                return _fetch(url, {
                  ...init,
                  body: JSON.stringify({ items: [mainItem, ...bogoItems] }),
                }, ...rest);
              }
            }
          } catch (e) {}
        }
        return _fetch(url, init, ...rest);
      };
    }

    // Returns free-item entries to inject into the add request, based on cached cart.
    _getBogoItemsForAdd(variantId, addedQty) {
      const items = [];
      for (const rule of this.rules) {
        if (!rule.buyVariants.includes(variantId)) continue;
        const ruleKey = String(rule.ruleIndex);

        const currentBuyQty = this.cachedCart.items.reduce((sum, item) => {
          if (item.properties?.[BOGO_PROP] === 'true') return sum;
          if (rule.buyVariants.includes(item.variant_id)) return sum + item.quantity;
          return sum;
        }, 0);
        const newBuyQty = currentBuyQty + addedQty;

        for (const fp of rule.freeProducts) {
          const alreadyInCart = this.cachedCart.items.some(
            (item) =>
              item.variant_id === fp.variantId &&
              item.properties?.[BOGO_PROP] === 'true' &&
              item.properties?.[BOGO_RULE_PROP] === ruleKey
          );
          // Only inject if the free item isn't already in cart.
          // If it's there but with a wrong quantity, the BOGO sync corrects it.
          if (!alreadyInCart && newBuyQty > 0) {
            items.push({
              id: fp.variantId,
              quantity: newBuyQty,
              properties: { [BOGO_PROP]: 'true', [BOGO_RULE_PROP]: ruleKey },
            });
          }
        }
      }
      return items;
    }

    // ---------------------------------------------------------------------------
    // Checkout lock (shown while BOGO sync is in progress)
    // ---------------------------------------------------------------------------

    lockCheckout() {
      document.querySelectorAll('.gokwik-checkout, .shopifycheckoutbtn').forEach((el) => {
        el.classList.add('bogo--loading');
      });
      document.querySelectorAll(
        '.gokwik-checkout button, .shopifycheckoutbtn button, button[name="checkout"]'
      ).forEach((btn) => {
        btn.disabled = true;
      });

      const links = document.getElementById('empty__cart__button');
      if (links && !links.querySelector('.bogo-applying-notice')) {
        const notice = document.createElement('p');
        notice.className = 'bogo-applying-notice';
        notice.textContent = 'Adding your FREE Gift 🎁 Kindly wait for 3 seconds';
        links.prepend(notice);
      }
    }

    unlockCheckout() {
      document.querySelectorAll('.gokwik-checkout, .shopifycheckoutbtn').forEach((el) => {
        el.classList.remove('bogo--loading');
      });
      document.querySelectorAll(
        '.gokwik-checkout button, .shopifycheckoutbtn button, button[name="checkout"]'
      ).forEach((btn) => {
        btn.disabled = false;
      });
      document.querySelectorAll('.bogo-applying-notice').forEach((el) => el.remove());
    }

    // ---------------------------------------------------------------------------
    // Sync — fallback for cases the interceptor can't handle (quantity adjustments,
    // removals, or when cachedCart wasn't ready on the first add)
    // ---------------------------------------------------------------------------

    async sync(knownCart) {
      if (this.isSyncing) return;
      this.isSyncing = true;
      this.lockCheckout();

      try {
        const cart = knownCart ?? await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        const sectionsData = await this.processRules(cart);
        if (sectionsData) this.applyCartSections(sectionsData);
      } catch (e) {
        console.error('[BOGO]', e);
      } finally {
        this.isSyncing = false;
        this.unlockCheckout();
      }
    }

    async processRules(cart) {
      const cartNotification = document.querySelector('cart-notification');
      const sections = cartNotification
        ? cartNotification.getSectionsToRender().map((s) => s.id)
        : [];

      const toAdd = [];
      const toChange = [];

      for (const rule of this.rules) {
        const ruleKey = String(rule.ruleIndex);

        const buyQty = cart.items.reduce((sum, item) => {
          if (item.properties?.[BOGO_PROP] === 'true') return sum;
          if (rule.buyVariants.includes(item.variant_id)) return sum + item.quantity;
          return sum;
        }, 0);

        for (const fp of rule.freeProducts) {
          const existing = cart.items.find(
            (item) =>
              item.variant_id === fp.variantId &&
              item.properties?.[BOGO_PROP] === 'true' &&
              item.properties?.[BOGO_RULE_PROP] === ruleKey
          );

          if (buyQty > 0) {
            if (!existing) {
              toAdd.push({ variantId: fp.variantId, quantity: buyQty, ruleKey });
            } else if (existing.quantity !== buyQty) {
              toChange.push({ key: existing.key, quantity: buyQty });
            }
          } else if (existing) {
            toChange.push({ key: existing.key, quantity: 0 });
          }
        }
      }

      if (!toAdd.length && !toChange.length) return null;

      const isRemoving = toAdd.length === 0 && toChange.some((c) => c.quantity === 0);
      document.querySelectorAll('.bogo-applying-notice').forEach((el) => {
        el.textContent = isRemoving ? 'Removing your free gift…' : 'Adding your FREE Gift 🎁 Kindly wait for 3 seconds';
      });

      let sectionsData;

      if (toAdd.length > 0) {
        // Settle quantity adjustments first, then batch-add new free items in ONE
        // request that also returns rendered section HTML.
        if (toChange.length > 0) {
          await Promise.all(toChange.map((c) => this.changeItem(c.key, c.quantity)));
        }

        const items = toAdd.map((a) => ({
          id: a.variantId,
          quantity: a.quantity,
          properties: { [BOGO_PROP]: 'true', [BOGO_RULE_PROP]: a.ruleKey },
        }));

        const response = await fetch(routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
        });
        sectionsData = await response.json();

        // Fallback: if /cart/add.js didn't return sections, fetch them separately.
        if (!sectionsData.sections) {
          const fallback = await fetch(routes.cart_update_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ sections, sections_url: window.location.pathname }),
          });
          sectionsData = await fallback.json();
        }
      } else {
        // Changes only — run in parallel, then render sections once.
        await Promise.all(toChange.map((c) => this.changeItem(c.key, c.quantity)));

        const response = await fetch(routes.cart_update_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ sections, sections_url: window.location.pathname }),
        });
        sectionsData = await response.json();
      }

      return sectionsData;
    }

    async changeItem(key, quantity) {
      const r = await fetch(routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      return r.json();
    }

    applyCartSections(data) {
      const cartNotification = document.querySelector('cart-notification');
      if (!cartNotification) return;

      cartNotification.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (el && data.sections?.[section.id]) {
          el.innerHTML = cartNotification.getSectionInnerHTML(data.sections[section.id]);
        }
      });

      if (typeof data.total_price === 'number') {
        cartNotification.updatePriceBreakdown?.(data.total_price);
      }

      const itemCount = data.item_count ?? (data.items?.length > 0 ? 1 : 0);
      if (itemCount === 0) {
        cartNotification.emptyCartItem?.classList.add('no-js-inline');
        cartNotification.emptyCartButtonDisable?.classList.add('no-js-inline');
        cartNotification.cartItemMessageShow?.classList.remove('no-js-inline');
        cartNotification.cartAddSuccess?.classList.add('no-js-inline');
        cartNotification.cartEmptyHead?.classList.remove('no-js-inline');
      } else {
        cartNotification.emptyCartItem?.classList.remove('no-js-inline');
        cartNotification.emptyCartButtonDisable?.classList.remove('no-js-inline');
        cartNotification.cartItemMessageShow?.classList.add('no-js-inline');
        cartNotification.cartAddSuccess?.classList.remove('no-js-inline');
        cartNotification.cartEmptyHead?.classList.add('no-js-inline');
      }
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    window._bogoManager = new BogoManager();
  });
})();
